import React, { useState, useEffect } from "react"
import cx from "classnames"

const STATES = {
  default: "border-gray-200 focus-within:outline-blue",
  error: "border-red-600 outline-red",
  success: "border-green-600 outline-green"
} as const

export type SliderState = keyof typeof STATES

export type SliderProps = {
  /** Input name attribute */
  name?: string
  /** Visual state of the slider border */
  state?: SliderState
  /** Additional CSS classes */
  className?: string
  /** Called on input events as the slider moves */
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void
  /** Called on change events when the slider value commits */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  /** Controlled value */
  value?: number
  /** Minimum value */
  min?: number
  /** Maximum value */
  max?: number
  /** Step increment */
  step?: number
  /** Accessible label */
  "aria-label"?: string
  /** Accessible value text */
  "aria-valuetext"?: string
  /** HTML id attribute */
  id?: string
}

/**
 * A range input slider with configurable min/max/step values and visual state feedback.
 * Supports default, error, and success border states.
 */
function Slider({
  name,
  state = "default",
  className: classNameProp,
  onInput,
  onChange,
  value,
  min,
  max,
  step,
  id,
  "aria-label": ariaLabel,
  "aria-valuetext": ariaValuetext
}: SliderProps) {
  const [rangeValue, setRangeValue] = useState(
    typeof value !== "undefined" ? value : min || 0
  )

  const className = cx(
    classNameProp,
    "design-slider p-2 flex items-center border rounded",
    STATES[state]
  )

  useEffect(() => {
    if (typeof value === "object" || typeof value === "undefined") return
    setRangeValue(value)
  }, [value])

  function updateProgress(event: React.FormEvent<HTMLInputElement>) {
    setRangeValue(Number((event.target as HTMLInputElement).value))
    if (onInput) {
      onInput(event)
    }
  }

  return (
    <div
      className={className}
      style={
        {
          transition: "border-color 0.3s, box-shadow 0.3s",
          "--min": min,
          "--max": max,
          "--rangeValue": rangeValue,
          "--progressColor": "#1e1e1e",
          "--thumbColor": "#1e1e1e",
          "--trackColor": "#d4d4d4"
        } as React.CSSProperties
      }
    >
      <input
        name={name}
        type="range"
        onInput={updateProgress}
        onChange={onChange}
        value={rangeValue}
        min={min}
        max={max}
        step={step}
        id={id}
        aria-label={ariaLabel}
        aria-valuetext={ariaValuetext}
      />
    </div>
  )
}

export { Slider }
export default Slider
